import { ShiftInviteRequestMessage } from "@src/appV2/Chat/ShiftInviteRequestMessage";
import { SendbirdChannelMessageProps } from "./chat.types";
import { ReviewSentHomeMessage } from "./ReviewSentHomeMessage";
import { SentHomeResponseMessage } from "./SentHomeResponseMessage";
import { MessageCustomTypes } from "@src/appV2/Chat/type";
import { PlacementApplicationMessage } from "./components/PlacementApplicationMessage";
import { isDefined } from "@clipboard-health/util-ts";
import { PlacementApplicationAutoInterviewInvite } from "./components/PlacementApplicationAutoInterviewInvite";
import { PlacementApplicationInvite } from "./components/PlacementApplicationInvite";
import { EveryMessage } from "@sendbird/uikit-react/types/types";
import { PlacementApplicationSuggestInterviewMessage } from "./components/PlacementApplicationSuggestInterviewMessage";
import { PlacementApplicationInterviewScheduledMessage } from "./components/PlacementApplicationInterviewScheduledMessage";
import {
  parseFacilityChatMessage,
  parseShiftInviteChatMessage,
  parseShiftTimeProposalMessage,
  parseVisitInviteChatMessage,
} from "../../appV2/Chat/utils";
import { PlacementApplicationInterviewCancelled } from "./components/PlacementApplicationInterviewCancelled";
import { WorkerCredentialRequestedMessage } from "@src/appV2/Chat/WorkerCredentialRequestedMessage";
import { IncomingUserChatMessage } from "../../appV2/Chat/IncomingUserChatMessage";
import { OutgoingUserChatMessage } from "../../appV2/Chat/OutgoingUserChatMessage";
import {
  isFacilityCanceledMeRequestMessage,
  isShiftInviteRequestMessage,
  isShiftTimeProposalMessage,
  isVisitInviteRequestMessage,
  shouldRenderDefaultAdminMessage,
} from "./utils/helpers";
import { isAdminMessage, isSendableMessage } from "./utils/helpers";
import { ShiftTimeProposalRequestMessage } from "../../appV2/Chat/ShiftTimeProposalRequestMessage";
import { VisitInviteRequestMessage } from "@src/appV2/Chat/VisitInviteRequestMessage";
import { SendbirdDefaultAdminMessage } from "./SendbirdDefaultAdminMessage";
import Message from "@sendbird/uikit-react/Channel/components/Message";
import { GroupShiftInviteRequestMessage } from "../../appV2/Chat/GroupShiftInviteRequestMessage";

// We are not able render this component as JSX with return type undefined
// as it's wrapped in an useMemo in it's source code
// [here](https://github.com/sendbird/sendbird-uikit-react/blob/main/src/modules/Channel/components/Message/index.tsx#L194)
export function renderSendbirdChannelMessage(props: SendbirdChannelMessageProps) {
  const {
    message,
    chainTop,
    chainBottom,
    userId,
    user,
    currentChannel,
    channelUrl,
    manageCredentialsModalState,
  } = props;

  const isFirstMessageInMessageChain = !chainTop;
  const isLastMessageInMessageChain = !chainBottom;

  const isDefinedCurrentChannelAndUrl = isDefined(currentChannel) && isDefined(channelUrl);

  if (shouldRenderDefaultAdminMessage(message)) {
    // Message payload has both worker and workplace facing messages, so parse the
    // the facility message and overwrite before rendering the default Sendbird admin message
    const parsedMessage = parseFacilityChatMessage(message);
    return <SendbirdDefaultAdminMessage message={parsedMessage} />;
  }

  if (isFacilityCanceledMeRequestMessage(message)) {
    return (
      <ReviewSentHomeMessage message={message} facilityId={userId} facilityUserId={user?._id} />
    );
  }

  if (isShiftInviteRequestMessage(message)) {
    const { shiftInviteId, status, facilityMessage } = parseShiftInviteChatMessage(message);

    // Group invites do not have a shift invite ID or status in the message
    const isGroupShiftInvite = !isDefined(shiftInviteId) || !isDefined(status);
    if (isGroupShiftInvite) {
      return <GroupShiftInviteRequestMessage message={message} />;
    }

    return (
      <ShiftInviteRequestMessage
        message={facilityMessage}
        shiftInviteId={shiftInviteId}
        status={status}
      />
    );
  }

  if (isVisitInviteRequestMessage(message)) {
    const { visitId } = parseVisitInviteChatMessage(message);
    return <VisitInviteRequestMessage visitId={visitId} />;
  }

  if (isShiftTimeProposalMessage(message)) {
    const parsedMessage = parseShiftTimeProposalMessage(message);

    if (!isDefined(parsedMessage)) {
      return <SendbirdDefaultAdminMessage message={message.message} />;
    }

    const { shiftTimeProposalId, originalStartTime, originalEndTime } = parsedMessage;

    return (
      <ShiftTimeProposalRequestMessage
        shiftTimeProposalId={shiftTimeProposalId}
        originalStartTime={originalStartTime}
        originalEndTime={originalEndTime}
      />
    );
  }

  if (
    message.customType === MessageCustomTypes.FCM_APPROVAL ||
    message.customType === MessageCustomTypes.FCM_REJECT
  ) {
    return <SentHomeResponseMessage message={message} />;
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION
  ) {
    return (
      <PlacementApplicationMessage
        message={message}
        currentChannel={currentChannel}
        channelUrl={channelUrl}
      />
    );
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION_SUGGEST_INTERVIEW
  ) {
    return (
      <PlacementApplicationSuggestInterviewMessage
        message={message}
        currentChannel={currentChannel}
        channelUrl={channelUrl}
      />
    );
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION_CHAT_INTERVIEW_SCHEDULED
  ) {
    return (
      <PlacementApplicationInterviewScheduledMessage
        message={message}
        currentChannel={currentChannel}
      />
    );
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION_AUTO_INTERVIEW_INVITE
  ) {
    return (
      <PlacementApplicationAutoInterviewInvite
        message={message}
        currentChannel={currentChannel}
        channelUrl={channelUrl}
      />
    );
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.INTERVIEW_WORKER_CANCELLATION
  ) {
    return (
      <PlacementApplicationInterviewCancelled
        currentChannel={currentChannel}
        channelUrl={channelUrl}
      />
    );
  }

  if (
    isDefined(currentChannel) &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION_INVITE
  ) {
    return <PlacementApplicationInvite message={message as EveryMessage} />;
  }

  if (
    isAdminMessage(message) &&
    isDefined(currentChannel) &&
    message.customType === MessageCustomTypes.WORKER_CREDENTIAL_REQUEST
  ) {
    return (
      <WorkerCredentialRequestedMessage
        message={message}
        workerName={currentChannel.metadata?.hcpName}
        manageCredentialsModalState={manageCredentialsModalState}
      />
    );
  }

  if (isSendableMessage(message)) {
    const { sender } = message;
    const sentByMe = sender?.userId === userId;

    if (!sentByMe) {
      return (
        <IncomingUserChatMessage
          message={message}
          isFirstMessageInMessageChain={isFirstMessageInMessageChain}
          isLastMessageInMessageChain={isLastMessageInMessageChain}
        />
      );
    }

    return (
      <OutgoingUserChatMessage
        message={message}
        isFirstMessageInMessageChain={isFirstMessageInMessageChain}
      />
    );
  }

  // Fallback to default Sendbird message
  return <Message message={message as EveryMessage} />;
}
